import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Users, ArrowLeft, PhoneOff, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { VideoTile } from '../components/VideoTile';
import { CallControls } from '../components/CallControls';
import { ParticipantSidebar } from '../components/ParticipantSidebar';
import { CaptionHistoryPanel } from '../components/CaptionHistoryPanel';
import { LanguageToggle } from '../components/LanguageToggle';
import { useTRTC } from '../hooks/useTRTC';
import { useViewport } from '../hooks/useViewport';
import { useLanguage } from '../context/LanguageContext';
import { generateUserId } from '../lib/roomId';
import { CALL } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CallRoom() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userIdRef = useRef(generateUserId());
  const localVideoRef = useRef(null);
  const { isMobile, isLandscape } = useViewport();
  const { t, lang } = useLanguage();

  const [name, setName] = useState(location.state?.name || '');
  const [title] = useState(location.state?.title || '');
  const [nameDraft, setNameDraft] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [participantNames, setParticipantNames] = useState({});
  const [pinnedKey, setPinnedKey] = useState(null);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const wasReconnecting = useRef(false);
  const hasJoinedOnce = useRef(false);
  const isHostRef = useRef(false);
  const joinedRef = useRef(false);
  const transcriptRef = useRef([]);
  const participantNamesRef = useRef({});
  const beaconFiredRef = useRef(false);
  const intentionalLeaveRef = useRef(false);

  const {
    joined,
    connecting,
    connectionState,
    reconnecting,
    remoteTiles,
    networkQuality,
    muted,
    cameraOff,
    screenSharing,
    autoplayBlocked,
    resumePlayback,
    isHost,
    transcript,
    error,
    inactivityWarningAt,
    autoEndSignal,
    bgBlurOn,
    bgBlurSupported,
    caption,
    captionHistory,
    captionsOn,
    toggleCaptionsOn,
    captionPanelOpen,
    toggleCaptionPanel,
    captionsFeatureEnabled,
    toggleCaptionsFeature,
    join,
    leave,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    toggleBgBlur,
  } = useTRTC();

  useEffect(() => { isHostRef.current = isHost; }, [isHost]);
  useEffect(() => { joinedRef.current = joined; }, [joined]);
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => { participantNamesRef.current = participantNames; }, [participantNames]);

  useEffect(() => {
    const handler = () => {
      if (!joinedRef.current || !isHostRef.current) return;
      fireBeaconSave();
    };
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('pagehide', handler);
      window.removeEventListener('beforeunload', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, name]);

  useEffect(() => {
    if (!name || hasJoinedOnce.current) return;
    hasJoinedOnce.current = true;
    join({ roomId, userId: userIdRef.current, name, localVideoEl: localVideoRef.current, title, appLang: lang }).catch(() => {});
    return () => {
      leave({ roomId, userId: userIdRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  useEffect(() => {
    if (!joined) return;
    const fetchNames = () => {
      axios
        .get(`${API}/rooms/${roomId}`)
        .then(({ data }) => {
          const map = {};
          (data.participants || []).forEach((p) => {
            map[p.user_id] = p.name;
          });
          setParticipantNames(map);
        })
        .catch(() => {});
    };
    fetchNames();
    const interval = setInterval(fetchNames, 5000);
    return () => clearInterval(interval);
  }, [joined, roomId]);

  useEffect(() => {
    if (reconnecting) {
      wasReconnecting.current = true;
      toast.warning(t('call.packetLoss'), { id: 'trtc-reconnect' });
    } else if (wasReconnecting.current && connectionState === 'CONNECTED') {
      wasReconnecting.current = false;
      toast.success(t('call.connectionRestored'), { id: 'trtc-reconnect' });
    }
  }, [reconnecting, connectionState]);

  useEffect(() => {
    if (error) toast.error(error, { id: 'trtc-error' });
  }, [error]);

  const remoteTileKeys = remoteTiles.map((t) => t.key);
  useEffect(() => {
    if (pinnedKey && !remoteTileKeys.includes(pinnedKey) && pinnedKey !== 'local-video') setPinnedKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteTileKeys.join(',')]);

  const downloadMeetingSummary = async () => {
    const fullTranscript = [...transcript].sort((a, b) => a.ts - b.ts);
    const participants = [...new Set([name, ...Object.values(participantNames)])];
    const toastId = 'meeting-summary';
    toast.info(t('call.savingRecord'), { id: toastId });
    try {
      const res = await axios.post(
        `${API}/meetings/end`,
        { room_id: roomId, participants, transcript: fullTranscript },
        { responseType: 'blob', withCredentials: true }
      );
      if (res.data?.type === 'application/pdf') {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `meeting-summary-${roomId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t('call.recordSavedWithPdf'), { id: toastId });
      } else {
        toast.success(t('call.recordSavedNoPdf'), { id: toastId });
      }
      // The record has been persisted server-side; suppress the beacon fallback.
      beaconFiredRef.current = true;
    } catch (e) {
      toast.error(t('call.recordSaveFailed'), { id: toastId });
      // POST failed. Fire beacon as fallback so the server still gets a chance to save.
      fireBeaconSave();
    }
  };

  const fireBeaconSave = () => {
    if (beaconFiredRef.current) return;
    if (!isHostRef.current) return;
    beaconFiredRef.current = true;
    try {
      const fullTranscript = [...transcriptRef.current].sort((a, b) => a.ts - b.ts);
      const participants = [...new Set([name, ...Object.values(participantNamesRef.current)])];
      const payload = { room_id: roomId, participants, transcript: fullTranscript };
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/meetings/end-beacon`, blob);
    } catch (e) {
      // beacon unavailable, nothing else to do
    }
  };

  const handleLeave = async () => {
    if (isHost && !intentionalLeaveRef.current) {
      setEndDialogOpen(true);
      return;
    }
    if (isHost) await downloadMeetingSummary();
    await leave({ roomId, userId: userIdRef.current });
    navigate('/');
  };

  const handleEndAndSave = async () => {
    setEndDialogOpen(false);
    intentionalLeaveRef.current = true;
    await downloadMeetingSummary();
    await leave({ roomId, userId: userIdRef.current });
    navigate('/');
  };

  const handleLeaveOnly = async () => {
    setEndDialogOpen(false);
    intentionalLeaveRef.current = true;
    // Host chose to leave silently — suppress beacon so we don't save minutes.
    beaconFiredRef.current = true;
    await leave({ roomId, userId: userIdRef.current });
    navigate('/');
  };

  useEffect(() => {
    if (!inactivityWarningAt) return;
    toast.warning(t('call.inactivityWarning'), {
      id: 'inactivity-warning',
      duration: 8000,
    });
  }, [inactivityWarningAt]);

  useEffect(() => {
    if (!autoEndSignal) return;
    toast.info(t('call.autoEnded'), { id: 'auto-end' });
    handleLeave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEndSignal]);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/room/${roomId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('call.copyLinkSuccess'));
    } catch (e) {
      toast.error(t('call.copyLinkError') + link);
    }
  };

  if (!name) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#F8FAFC] px-6">
        <div className="absolute right-6 top-6">
          <LanguageToggle />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (nameDraft.trim()) setName(nameDraft.trim());
          }}
          className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
        >
          <p className="mb-1 font-mono text-xs uppercase tracking-[0.2em] text-[#3B82F6]">{t('call.room')} {roomId}</p>
          <h2 className="mb-6 font-heading text-2xl font-semibold text-slate-900">{t('call.enterNameTitle')}</h2>
          <Input
            data-testid="call-join-name-input"
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            placeholder={t('call.namePlaceholder')}
            className="mb-4 h-12 border-slate-200 bg-slate-50 text-slate-900 focus-visible:ring-[#3B82F6]"
          />
          <Button
            type="submit"
            data-testid="call-join-name-btn"
            className="h-12 w-full rounded-full bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90"
          >
            {t('call.join')}
          </Button>
        </form>
      </div>
    );
  }

  const local = networkQuality.local || {};
  const uniqueRemoteUserIds = [...new Set(remoteTiles.map((t) => t.userId))];
  const totalParticipants = uniqueRemoteUserIds.length + 1;

  const tiles = [
    {
      key: 'local-video',
      domId: 'local-video',
      testId: CALL.localTile,
      name,
      isLocal: true,
      hasVideo: !cameraOff,
      hasAudio: !muted,
      networkScore: local.uplink,
      videoRef: localVideoRef,
    },
    ...remoteTiles.map((t) => ({
      key: t.key,
      domId: t.key,
      testId: CALL.remoteTile(t.key),
      name:
        t.streamType === 'sub'
          ? `${participantNames[t.userId] || t.userId} (screen)`
          : participantNames[t.userId] || t.userId,
      isLocal: false,
      hasVideo: t.hasVideo,
      hasAudio: t.hasAudio,
      networkScore: networkQuality[t.userId] ?? local.downlink,
    })),
  ];

  const others = tiles.filter((t) => t.key !== pinnedKey);
  const isMobilePortraitPinned = isMobile && !isLandscape && !!pinnedKey;
  const stripWidth = isMobile ? 132 : 192;
  const thumbWidth = isMobile ? 108 : 168;
  const thumbHeight = Math.max(64, Math.min(104, Math.floor(560 / Math.max(others.length, 1)) - 12));

  const sidebarParticipants = [
    { userId: userIdRef.current, name, isLocal: true, hasAudio: !muted },
    ...uniqueRemoteUserIds.map((uid) => {
      const mainTile = remoteTiles.find((t) => t.userId === uid && t.streamType !== 'sub');
      return {
        userId: uid,
        name: participantNames[uid] || uid,
        isLocal: false,
        hasAudio: mainTile?.hasAudio ?? false,
      };
    }),
  ];

  return (
    <div data-testid={CALL.container} className="relative flex h-screen w-screen flex-col overflow-hidden bg-[#F8FAFC]">
      <header className="flex items-center justify-between px-4 py-4 sm:px-6 sm:py-5">
        <button
          onClick={handleLeave}
          className="flex items-center gap-2 rounded-full py-2 pr-2 font-body text-sm text-slate-500 transition-colors hover:text-slate-900"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="h-4 w-4" /> {t('call.leave')}
        </button>
        <div className="flex items-center gap-2 sm:gap-4">
          <LanguageToggle className="hidden sm:inline-flex" />
          <LanguageToggle compact className="sm:hidden" />
          <span data-testid={CALL.roomIdLabel} className="hidden font-mono text-sm text-slate-500 sm:inline">
            {t('call.room')} <span className="text-slate-900">{roomId}</span>
          </span>
          <span
            data-testid={CALL.statusBadge}
            className={`rounded-full px-3 py-1.5 font-mono text-xs ${
              connectionState === 'CONNECTED'
                ? 'bg-emerald-50 text-emerald-600'
                : connectionState === 'RECONNECTING'
                ? 'bg-amber-50 text-amber-600'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {t(`call.status.${connectionState}`)}
            {local.uplinkRTT !== undefined ? ` · ${local.uplinkRTT}ms` : ''}
          </span>
          <button
            data-testid={CALL.toggleSidebarBtn}
            onClick={() => setSidebarOpen((o) => !o)}
            className={`flex items-center gap-1 rounded-full px-3 py-2 font-mono text-sm transition-colors ${
              sidebarOpen ? 'bg-[#3B82F6]/10 text-[#3B82F6]' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Users className="h-4 w-4" />
            <span data-testid={CALL.participantCount}>{totalParticipants}</span>
          </button>
        </div>
      </header>

      <main
        data-testid={CALL.videoGrid}
        className={
          pinnedKey
            ? 'relative flex-1 overflow-hidden px-3 pb-24 sm:px-6 sm:pb-28'
            : 'relative grid flex-1 auto-rows-fr gap-3 overflow-y-auto px-3 pb-24 sm:gap-4 sm:px-6 sm:pb-28'
        }
        style={!pinnedKey ? { gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 130 : 220}px, 1fr))` } : undefined}
      >
        <AnimatePresence>
          {tiles.map((t) => {
            const isPinned = pinnedKey === t.key;
            const thumbIndex = others.findIndex((o) => o.key === t.key);
            let wrapperStyle;
            if (!pinnedKey) {
              wrapperStyle = undefined;
            } else if (isMobilePortraitPinned) {
              const mobileThumbWidth = Math.max(48, Math.min(84, Math.floor(340 / Math.max(others.length, 1)) - 8));
              wrapperStyle = isPinned
                ? { position: 'absolute', top: 100, left: 0, right: 0, bottom: 0 }
                : { position: 'absolute', top: 8, left: thumbIndex * (mobileThumbWidth + 8), width: mobileThumbWidth, height: 76 };
            } else {
              wrapperStyle = isPinned
                ? { position: 'absolute', top: 0, bottom: 0, left: 0, right: stripWidth }
                : { position: 'absolute', top: thumbIndex * (thumbHeight + 12), right: 0, width: thumbWidth, height: thumbHeight };
            }
            return (
              <div key={t.key} style={wrapperStyle}>
                <VideoTile
                  domId={t.domId}
                  testId={t.testId}
                  name={t.name}
                  isLocal={t.isLocal}
                  hasVideo={t.hasVideo}
                  hasAudio={t.hasAudio}
                  networkScore={t.networkScore}
                  videoRef={t.videoRef}
                  fill={!!pinnedKey}
                  pinned={isPinned}
                  onClick={() => setPinnedKey(isPinned ? null : t.key)}
                />
              </div>
            );
          })}
        </AnimatePresence>
      </main>

      {connecting && !joined && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#F8FAFC]/90 font-body text-slate-500">
          {t('call.connectingOverlay')}
        </div>
      )}

      {autoplayBlocked && (
        <button
          data-testid="resume-playback-btn"
          onClick={resumePlayback}
          className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 font-body text-sm text-[#F8FAFC]"
        >
          <span className="rounded-full bg-[#3B82F6] px-6 py-3 hover:bg-[#3B82F6]/90">
            {t('call.autoplayBlocked')}
          </span>
        </button>
      )}

      {captionsFeatureEnabled && captionsOn && caption && (
        <div
          data-testid={CALL.captionBar}
          className="pointer-events-none absolute inset-x-0 bottom-24 z-20 flex justify-center px-4 sm:bottom-28"
        >
          <div className="max-w-2xl rounded-2xl bg-black/75 px-5 py-3 text-center shadow-lg backdrop-blur-md">
            <p className="mb-0.5 font-mono text-[10px] uppercase tracking-wide text-white/50">{caption.speaker}</p>
            {caption.pending ? (
              <p className="flex justify-center gap-1 py-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70 [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70 [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70" />
              </p>
            ) : (
              <p className="notranslate font-body text-lg font-bold text-white sm:text-xl" lang="zh-CN" translate="no">{caption.translated}</p>
            )}
          </div>
        </div>
      )}

      <ParticipantSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        participants={sidebarParticipants}
        isMobile={isMobile}
      />

      <CaptionHistoryPanel
        open={captionsFeatureEnabled && captionPanelOpen}
        onClose={toggleCaptionPanel}
        history={captionHistory}
        isMobile={isMobile}
      />

      <CallControls
        muted={muted}
        cameraOff={cameraOff}
        screenSharing={screenSharing}
        bgBlurOn={bgBlurOn}
        bgBlurSupported={bgBlurSupported}
        captionsOn={captionsOn}
        captionPanelOpen={captionPanelOpen}
        captionsFeatureEnabled={captionsFeatureEnabled}
        isHost={isHost}
        onToggleMic={toggleMic}
        onToggleCamera={() => toggleCamera(localVideoRef.current)}
        onToggleScreenShare={toggleScreenShare}
        onToggleBlur={toggleBgBlur}
        onToggleCaptions={toggleCaptionsOn}
        onToggleCaptionPanel={toggleCaptionPanel}
        onToggleCaptionsFeature={() => toggleCaptionsFeature(roomId)}
        onLeave={handleLeave}
        onCopyLink={handleCopyLink}
        isMobile={isMobile}
      />

      {endDialogOpen && (
        <div
          data-testid={CALL.endMeetingDialog}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setEndDialogOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 font-heading text-xl font-semibold text-slate-900">
              {t('call.endMeetingConfirmTitle')}
            </h3>
            <p className="mb-6 font-body text-sm leading-relaxed text-slate-600">
              {t('call.endMeetingConfirmDesc')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                data-testid={CALL.endMeetingConfirmBtn}
                onClick={handleEndAndSave}
                className="flex h-11 items-center justify-center gap-2 rounded-full bg-red-500 px-5 font-medium text-white transition-colors hover:bg-red-600"
              >
                <PhoneOff className="h-4 w-4" />
                {t('call.endMeetingConfirmBtn')}
              </button>
              <button
                type="button"
                data-testid={CALL.endMeetingLeaveOnlyBtn}
                onClick={handleLeaveOnly}
                className="flex h-11 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                <LogOut className="h-4 w-4" />
                {t('call.leaveOnlyBtn')}
              </button>
              <button
                type="button"
                data-testid={CALL.endMeetingCancelBtn}
                onClick={() => setEndDialogOpen(false)}
                className="h-11 rounded-full font-medium text-slate-500 transition-colors hover:text-slate-900"
              >
                {t('call.cancelBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

